import {
  Instant,
  DateTimeFormatter,
  ZoneId,
  LocalDate,
  ChronoUnit,
  TemporalAdjusters,
} from "@js-joda/core";
import { Locale } from "@js-joda/locale_en";
import "@js-joda/timezone";
import { DateFilter, DateFilterType, DatePeriodEnum, MonthEnum, QuarterEnum } from "build-ts/relative_date";
import { DateFilterCategory } from "./enum";

/**
 * Converts epoch milliseconds to a formatted date string in IST.
 * @param {number} num - Epoch milliseconds.
 * @returns {string} Formatted date string in IST.
 */
export const epochConverter = (num: number): string => {
  const instant = Instant.ofEpochMilli(num);
  const formatter = DateTimeFormatter.ofPattern("d-MMM-yyyy, HH:mm").withLocale(Locale.US);
  return instant.atZone(ZoneId.of("Asia/Kolkata")).format(formatter);
};

/**
 * Converts epoch second to a formatted date string with given time zone.
 * @param {number} num - Epoch milliseconds.
 * @param {string} format - Format of the date string.
 * @param {string} defaultTimeZone - Time zone of the date string.
 * @returns {string} Formatted date string with given time zone.
 */
export function epochConverterWithTimeZone(num: number, format: string, defaultTimeZone: string): string {
  const instant = Instant.ofEpochMilli(num);
  const formatter = DateTimeFormatter.ofPattern(format).withLocale(Locale.US);
  return instant.atZone(ZoneId.of(defaultTimeZone)).format(formatter);
};

/**
 * Converts epoch second to a formatted date string in IST.
 * @param {number} num - Epoch seconds.
 * @returns {string} Formatted date string in IST.
 */
export const epochConverterSecond = (num: number): string => {
  const instant = Instant.ofEpochMilli(num * 1000);
  const formatter = DateTimeFormatter.ofPattern("d-MMM-yyyy").withLocale(Locale.US);
  return instant.atZone(ZoneId.of("Asia/Kolkata")).format(formatter);
};

/**
 * Converts epoch second to a formatted date string with given time zone.
 * @param {number} num - Epoch seconds.
 * @param {string} format - Format of the date string.
 * @param {string} defaultTimeZone - Time zone of the date string.
 * @returns {string} Formatted date string with given time zone.
 */
export function epochConverterSecondWithTimeZone(num: number, format: string, defaultTimeZone: string): string {
  const instant = Instant.ofEpochMilli(num * 1000);
  const formatter = DateTimeFormatter.ofPattern(format).withLocale(Locale.US);
  return instant.atZone(ZoneId.of(defaultTimeZone)).format(formatter);
};

/**
 * Parses a date string in MM/DD/YYYY format to a LocalDate object.
 * @param {string} dateStr - Date string in MM/DD/YYYY format.
 * @returns {LocalDate} Parsed LocalDate object.
 */
const parseDate = (dateStr: string): LocalDate => {
  const formatter = DateTimeFormatter.ofPattern("MM/dd/yyyy");
  return LocalDate.parse(dateStr, formatter);
};

/**
 * Formats a LocalDate object back to MM/DD/YYYY string.
 * @param {LocalDate} date - LocalDate object.
 * @returns {string} Formatted date string in MM/DD/YYYY.
 */
const formatDate = (date: LocalDate): string => {
  const formatter = DateTimeFormatter.ofPattern("MM/dd/yyyy");
  return date.format(formatter);
};

/**
 * Determines the period type based on the difference between startDate and endDate.
 * @param {LocalDate} startDate - Start date of the period.
 * @param {LocalDate} endDate - End date of the period.
 * @returns {string} Period type: 'weekly', 'monthly', 'quarterly', 'yearly'.
 * @throws {Error} If the period type is unsupported.
 */
const determinePeriodType = (
  startDate: LocalDate,
  endDate: LocalDate
): "weekly" | "monthly" | "quarterly" | "yearly" => {
  const durationInWeeks = ChronoUnit.WEEKS.between(startDate, endDate) + 1;
  if (durationInWeeks === 1) {
    return "weekly";
  }

  const durationInMonths =
    endDate.monthValue() - startDate.monthValue() + 1 + // Adding 1 to include both months
    (endDate.year() - startDate.year()) * 12;
  if (durationInMonths === 1) {
    return "monthly";
  } else if (durationInMonths === 3) {
    return "quarterly";
  } else if (durationInMonths === 12) {
    return "yearly";
  } else {
    throw new Error("Unsupported period type");
  }
};

/**
 * Calculates the previous period based on the current period type.
 * @param {LocalDate} startDate - Start date of the current period.
 * @param {LocalDate} endDate - End date of the current period.
 * @param {string} periodType - Type of the current period: 'weekly', 'monthly', 'quarterly', 'yearly'.
 * @returns {{ gte: LocalDate; lte: LocalDate }} Previous period's start and end dates.
 */
const getPreviousPeriod = (
  startDate: LocalDate,
  endDate: LocalDate,
  periodType: "weekly" | "monthly" | "quarterly" | "yearly"
): { gte: LocalDate; lte: LocalDate } => {
  let startDatePreviousPeriod: LocalDate;
  let endDatePreviousPeriod: LocalDate;

  switch (periodType) {
    case "weekly":
      // Previous week: subtract 1 week from startDate and endDate
      startDatePreviousPeriod = startDate.minusWeeks(1);
      endDatePreviousPeriod = endDate.minusWeeks(1);
      break;

    case "monthly":
      // Previous month: subtract 1 month from startDate and set endDate to last day of previous month
      startDatePreviousPeriod = startDate.minusMonths(1);
      endDatePreviousPeriod = startDatePreviousPeriod.with(TemporalAdjusters.lastDayOfMonth());
      break;

    case "quarterly":
      // Previous quarter: subtract 3 months from startDate and set endDate to last day of the previous quarter
      startDatePreviousPeriod = startDate.minusMonths(3);
      endDatePreviousPeriod = startDatePreviousPeriod.plusMonths(2).with(TemporalAdjusters.lastDayOfMonth());
      break;

    case "yearly":
      // Previous year: subtract 1 year from startDate and endDate
      startDatePreviousPeriod = startDate.minusYears(1);
      endDatePreviousPeriod = endDate.minusYears(1);
      break;

    default:
      throw new Error("Unsupported period type");
  }

  return { gte: startDatePreviousPeriod, lte: endDatePreviousPeriod };
};

/**
 * Calculates the same period last year based on the current period type.
 * @param {LocalDate} startDate - Start date of the current period.
 * @param {LocalDate} endDate - End date of the current period.
 * @param {string} periodType - Type of the current period: 'weekly', 'monthly', 'quarterly', 'yearly'.
 * @returns {{ gte: LocalDate; lte: LocalDate }} Same period last year's start and end dates.
 */
const getSamePeriodLastYear = (
  startDate: LocalDate,
  endDate: LocalDate,
  periodType: "weekly" | "monthly" | "quarterly" | "yearly"
): { gte: LocalDate; lte: LocalDate } => {
  let gte: LocalDate;
  let lte: LocalDate;

  switch (periodType) {
    case "weekly":
      gte = startDate.minusYears(1);
      lte = endDate.minusYears(1);
      break;

    case "monthly":
      gte = startDate.minusYears(1);
      lte = endDate.minusYears(1);
      break;

    case "quarterly":
      gte = startDate.minusYears(1);
      lte = endDate.minusYears(1);
      break;

    case "yearly":
      gte = startDate.minusYears(1);
      lte = endDate.minusYears(1);
      break;

    default:
      throw new Error("Unsupported period type");
  }

  return { gte, lte };
};

/**
 * Computes the previous period and the same period last year based on the provided date range.
 * Supports weekly, monthly, quarterly, and yearly periods.
 * @param {string} gte - Start date in MM/DD/YYYY format.
 * @param {string} lte - End date in MM/DD/YYYY format.
 * @returns {{
 *   samePeriodLastYear: { gte: string; lte: string };
 *   previousPeriod: { gte: string; lte: string };
 * }}
 * Formatted date ranges for the same period last year and the previous period.
 */
export const getDateRanges = (
  gte: string,
  lte: string
): {
  samePeriodLastYear: { gte: string; lte: string };
  previousPeriod: { gte: string; lte: string };
} => {
  const startDate = parseDate(gte);
  const endDate = parseDate(lte);

  // Determine the period type
  const periodType = determinePeriodType(startDate, endDate);

  // Calculate previous period
  const previousPeriodDates = getPreviousPeriod(startDate, endDate, periodType);

  // Calculate same period last year
  const samePeriodLastYearDates = getSamePeriodLastYear(
    startDate,
    endDate,
    periodType
  );

  return {
    samePeriodLastYear: {
      gte: formatDate(samePeriodLastYearDates.gte),
      lte: formatDate(samePeriodLastYearDates.lte),
    },
    previousPeriod: {
      gte: formatDate(previousPeriodDates.gte),
      lte: formatDate(previousPeriodDates.lte),
    },
  };
};

/**
 * Validates date filter.
 * @param {DateFilter} filter - date filter.
 * @returns true if dateFilter is valid otherwise false.
 */
export const isDateFilterValid = (filter: DateFilter): boolean => {
  // Check if the type is undefined or unrecognized
  if (filter.type === undefined || filter.type === DateFilterType.UNRECOGNIZED) {
    return false;
  }
  switch (filter.type) {
    case DateFilterType.LAST_N_PERIOD:
    case DateFilterType.NEXT_N_PERIOD:
      // Ensure 'number' is a positive integer and 'date_period' is defined
      return (
        typeof filter.number === "number" &&
        filter.number > 0 &&
        filter.date_period !== undefined &&
        filter.date_period !== DatePeriodEnum.UNRECOGNIZED
      );
    case DateFilterType.THIS_PERIOD:
      // Ensure 'date_period' is defined and recognized
      return (
        filter.date_period !== undefined &&
        filter.date_period !== DatePeriodEnum.UNRECOGNIZED
      );
    case DateFilterType.EXACT_DATE_RANGE:
      // Ensure 'date_range' is defined and both low and high epoch seconds are valid
      return (
        filter.date_range !== undefined &&
        typeof filter.date_range.low_epoch_sec === "number" &&
        typeof filter.date_range.high_epoch_sec === "number" &&
        filter.date_range.low_epoch_sec <= filter.date_range.high_epoch_sec
      );
    case DateFilterType.PERIOD_TO_DATE:
      // Ensure 'date_period' is defined and recognized
      return (
        filter.date_period !== undefined &&
        filter.date_period !== DatePeriodEnum.UNRECOGNIZED
      );
    case DateFilterType.MONTH_YEAR:
      // Ensure 'month' and 'year' are defined and 'month' is recognized
      return (
        filter.month !== undefined &&
        filter.month !== MonthEnum.UNRECOGNIZED &&
        typeof filter.year === "number"
      );
    case DateFilterType.QUARTER_YEAR:
      // Ensure 'quarter' and 'year' are defined and 'quarter' is recognized
      return (
        filter.quarter !== undefined &&
        filter.quarter !== QuarterEnum.UNRECOGNIZED &&
        typeof filter.year === "number"
      );
    case DateFilterType.YEAR_ONLY:
      // Ensure 'year' is defined and is a valid number
      return typeof filter.year === "number";
    default:
      // For any other cases, consider the filter invalid
      return false;
  }
};

/**
 * Determines the category of a date filter for a given diagnostic plan.
 *
 * @param {DateFilter} dateFilter - Date filter.
 * @returns {DateFilterCategory} - The category of the date filter, either FIXED or ROLLING.
 */

export const getDateFilterCategory = (dateFilter: DateFilter)
: DateFilterCategory => {
  const fixedDateFilterTypes = [
    DateFilterType.EXACT_DATE,
    DateFilterType.EXACT_DATE_RANGE,
    DateFilterType.MONTH_YEAR,
    DateFilterType.QUARTER_YEAR,
    DateFilterType.YEAR_ONLY,
  ];

  if (fixedDateFilterTypes.includes(dateFilter?.type!)) {
    return DateFilterCategory.FIXED;
  }

  return DateFilterCategory.ROLLING;
};